import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/hooks/useAuth";
import { ProProvider } from "@/hooks/usePro";
import { NotebookProvider } from "@/hooks/useNotebook";
import { IdeaLimitProvider } from "@/hooks/useIdeaLimit";
import { AIResourcesProvider } from "@/hooks/useAIResources";
import { MarketplaceUnlockProvider } from "@/hooks/useMarketplaceUnlock";
import { WhatsAppPopup } from "@/components/WhatsAppPopup";
import { AuthCallback } from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Learn from "@/pages/Learn";
import Categories from "@/pages/Categories";
import CategoryDetail from "@/pages/CategoryDetail";
import BusinessIdeas from "@/pages/BusinessIdeas";
import BusinessDetail from "@/pages/BusinessDetail";
import StartupNotebook from "@/pages/StartupNotebook";
import Profile from "@/pages/Profile";
import Apps from "@/pages/Apps";
import AppDetail from "@/pages/AppDetail";
import BuildHub from "@/pages/BuildHub";
import DiscoveryHub from "@/pages/DiscoveryHub";
import Trending from "@/pages/Trending";
import CustomAppDetail from "@/pages/CustomAppDetail";
import ProSuccess from "@/pages/ProSuccess";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import PreviewProjectDetail from "@/pages/PreviewProjectDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProjectDetail from "@/pages/AdminProjectDetail";
import AdminContinueBuilding from "@/pages/AdminContinueBuilding";
import AdminContinueBuildingDetail from "@/pages/AdminContinueBuildingDetail";
import AdminSettings from "@/pages/AdminSettings";
import AdminRequests from "@/pages/AdminRequests";
import AdminStartups from "@/pages/AdminStartups";
import AdminEmail from "@/pages/AdminEmail";
import ContinueBuilding from "@/pages/ContinueBuilding";
import AccountFavorites from "@/pages/account/AccountFavorites";
import AccountProjects from "@/pages/account/AccountProjects";
import AccountPlans from "@/pages/account/AccountPlans";
import AccountPlanDetails from "@/pages/account/AccountPlanDetails";
import AccountInvoices from "@/pages/account/AccountInvoices";
import AccountPaymentMethods from "@/pages/account/AccountPaymentMethods";
import AccountSettings from "@/pages/account/AccountSettings";
import AIResourcesHome from "@/pages/ai/AIResourcesHome";
import AIWorkflow from "@/pages/ai/AIWorkflow";
import AIToolStackAudit from "@/pages/ai/AIToolStackAudit";
import AIToolRecommendations from "@/pages/ai/AIToolRecommendations";
import ValidationLab from "@/pages/ai/ValidationLab";
import CompetitorSearch from "@/pages/ai/CompetitorSearch";
import MarketSizeCalculator from "@/pages/ai/MarketSizeCalculator";
import OpportunityScanner from "@/pages/ai/OpportunityScanner";
import FounderRoadmap from "@/pages/ai/FounderRoadmap";
import NameChecker from "@/pages/ai/NameChecker";
import InfoPage from "@/pages/InfoPage";
import InfluencerMarketing from "@/pages/InfluencerMarketing";
import BusinessFormation from "@/pages/BusinessFormation";
import IdeaHub from "@/pages/IdeaHub";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppRoutes() {
  // Handle the OAuth return (session_id in URL fragment) before anything else.
  if (window.location.hash && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Apps />} />
      <Route path="/home" element={<Home />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/categories/:slug" element={<CategoryDetail />} />
      <Route path="/business-ideas" element={<BusinessIdeas />} />
      <Route path="/business/:id" element={<BusinessDetail />} />
      <Route path="/notebook" element={<StartupNotebook />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/apps" element={<Apps />} />
      <Route path="/apps/:id" element={<AppDetail />} />
      <Route path="/build" element={<BuildHub />} />
      <Route path="/discover" element={<DiscoveryHub />} />
      <Route path="/trending" element={<Trending />} />
      <Route path="/custom-app/:id" element={<CustomAppDetail />} />
      <Route path="/pro/success" element={<ProSuccess />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/preview-project/:id" element={<PreviewProjectDetail />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/projects/:id" element={<AdminProjectDetail />} />
      <Route path="/admin/requests" element={<AdminRequests />} />
      <Route path="/admin/continue-building" element={<AdminContinueBuilding />} />
      <Route path="/admin/continue-building/:id" element={<AdminContinueBuildingDetail />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/startups" element={<AdminStartups />} />
      <Route path="/admin/email" element={<AdminEmail />} />
      <Route path="/continue-building" element={<ContinueBuilding />} />
      <Route path="/account/favorites" element={<AccountFavorites />} />
      <Route path="/account/projects" element={<AccountProjects />} />
      <Route path="/account/plans" element={<AccountPlans />} />
      <Route path="/account/plans/details" element={<AccountPlanDetails />} />
      <Route path="/account/invoices" element={<AccountInvoices />} />
      <Route path="/account/payment-methods" element={<AccountPaymentMethods />} />
      <Route path="/account/settings" element={<AccountSettings />} />
      <Route path="/ai-resources" element={<AIResourcesHome />} />
      <Route path="/ai-resources/workflow" element={<AIWorkflow />} />
      <Route path="/ai-resources/tool-stack" element={<AIToolStackAudit />} />
      <Route path="/ai-resources/tool-recommendations" element={<AIToolRecommendations />} />
      <Route path="/ai-resources/validation-lab" element={<ValidationLab />} />
      <Route path="/ai-resources/competitor-search" element={<CompetitorSearch />} />
      <Route path="/ai-resources/market-size" element={<MarketSizeCalculator />} />
      <Route path="/ai-resources/roadmap" element={<FounderRoadmap />} />
      <Route path="/ai-resources/name-checker" element={<NameChecker />} />
      <Route path="/ai-resources/opportunity-scanner" element={<OpportunityScanner />} />
      <Route path="/legal/:slug" element={<InfoPage />} />
      <Route path="/help" element={<InfoPage pageKey="help" />} />
      <Route path="/faq" element={<InfoPage pageKey="faq" />} />
      <Route path="/resources/influencer-marketing" element={<InfluencerMarketing />} />
      <Route path="/resources/business-formation" element={<BusinessFormation />} />
      <Route path="/validate" element={<IdeaHub />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ProProvider>
            <IdeaLimitProvider>
              <NotebookProvider>
                <AIResourcesProvider>
                  <MarketplaceUnlockProvider>
                    <AppRoutes />
                    <WhatsAppPopup />
                  </MarketplaceUnlockProvider>
                </AIResourcesProvider>
              </NotebookProvider>
            </IdeaLimitProvider>
          </ProProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
